:root {
  color-scheme: light;
  /* Warm tabletop palette — bright cream surface, paper-white forms,
     deep brown ink, saturated UNO accents. */
  --bg: #fff3d6;
  --bg-soft: #fff8e6;
  --bg-2: #ffffff;
  --bg-3: #f0d9a4;
  --fg: #2a1a0a;
  --muted: #8a6a4a;
  --accent: #dc2626;
  --accent-strong: #991b1b;
  --danger: #b91c1c;
  --ok: #16a34a;
  --red: #ef4444;
  --yellow: #facc15;
  --green: #22c55e;
  --blue: #2563eb;
  --wild: #1f2937;
  --shadow: 0 6px 18px rgba(80, 40, 0, 0.18);
  --shadow-sm: 0 2px 6px rgba(80, 40, 0, 0.14);
  --radius: 14px;
}
* { box-sizing: border-box; }
html, body, #root {
  margin: 0;
  padding: 0;
  background:
    radial-gradient(circle at 20% 10%, #fff7e0 0%, transparent 55%),
    radial-gradient(circle at 80% 90%, #ffeec0 0%, transparent 60%),
    var(--bg);
  background-attachment: fixed;
  color: var(--fg);
  font-family: ui-rounded, "SF Pro Rounded", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  min-height: 100vh;
  -webkit-tap-highlight-color: transparent;
}
button {
  font: inherit;
  cursor: pointer;
  background: var(--accent);
  color: #fff;
  border: 0;
  padding: 0.85rem 1.3rem;
  border-radius: 12px;
  min-height: 48px;
  min-width: 48px;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: var(--shadow-sm);
  transition: transform 80ms ease, filter 100ms, box-shadow 120ms;
}
button:hover { filter: brightness(1.06); }
button:active { transform: translateY(1px); box-shadow: 0 1px 3px rgba(80,40,0,0.18); }
button:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }
button.ghost {
  background: var(--bg-2);
  color: var(--fg);
  border: 2px solid var(--bg-3);
  box-shadow: var(--shadow-sm);
}
button.danger { background: var(--danger); color: #fff; }
button.success { background: var(--ok); color: #fff; }
input, select {
  font: inherit;
  width: 100%;
  background: #fffdf6;
  color: var(--fg);
  border: 2px solid var(--bg-3);
  border-radius: 12px;
  padding: 0.75rem 0.9rem;
  min-height: 48px;
  outline: none;
  transition: border-color 120ms, box-shadow 120ms;
}
input:focus, select:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.15);
}
label { display: block; }
label > .muted { margin-bottom: 0.35rem; font-size: 0.85rem; }
.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  gap: 1rem;
}
.card {
  background: var(--bg-2);
  padding: 1.6rem;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  border: 1px solid #f3e3bf;
  width: 100%;
  max-width: 460px;
}
.brand {
  font-size: 2.4rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--accent);
  text-shadow: 0 2px 0 #fff8;
}
.muted { color: var(--muted); }
.danger-text { color: var(--danger); font-weight: 600; }
.row { display: flex; gap: 0.8rem; align-items: center; flex-wrap: wrap; }
.col { display: flex; flex-direction: column; gap: 1rem; }
.right { margin-left: auto; }
.fullscreen { position: fixed; inset: 0; }

/* TDC layout */
.tdc-root {
  display: grid;
  grid-template-rows: auto 1fr auto;
  height: 100vh;
}
.tdc-header {
  display: flex;
  align-items: center;
  padding: 0.9rem 1.4rem;
  gap: 1rem;
  background: rgba(255, 248, 230, 0.92);
  border-bottom: 2px solid var(--bg-3);
  backdrop-filter: blur(6px);
}
.tdc-table {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tdc-center {
  display: flex;
  gap: 1.5rem;
  align-items: center;
}
.tdc-footer {
  padding: 0.7rem 1.2rem;
  border-top: 2px solid var(--bg-3);
  display: flex;
  gap: 0.7rem;
  align-items: center;
  background: rgba(255, 248, 230, 0.92);
  backdrop-filter: blur(6px);
}
.tdc-footer-start {
  flex: 1;
  font-size: 1.15rem;
  font-weight: 700;
  padding: 0.9rem 1.4rem;
  min-height: 52px;
  border-radius: 12px;
}
.tdc-footer-start:disabled { opacity: 0.55; cursor: not-allowed; }
.tdc-footer-start.is-starting {
  opacity: 1;
  cursor: progress;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  background: linear-gradient(110deg,
    var(--ok, #16a34a) 0%,
    var(--ok, #16a34a) 35%,
    rgba(255, 255, 255, 0.45) 50%,
    var(--ok, #16a34a) 65%,
    var(--ok, #16a34a) 100%);
  background-size: 220% 100%;
  animation: btn-shimmer 1.4s linear infinite;
}
@keyframes btn-shimmer {
  0%   { background-position: 120% 0; }
  100% { background-position: -120% 0; }
}
.btn-spinner {
  width: 1.1rem;
  height: 1.1rem;
  border-radius: 50%;
  border: 2.5px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  animation: btn-spin 0.8s linear infinite;
}
@keyframes btn-spin {
  to { transform: rotate(360deg); }
}
@media (prefers-reduced-motion: reduce) {
  .tdc-footer-start.is-starting { animation: none; }
  .btn-spinner { animation: btn-spin 1.5s linear infinite; }
}

/* Seat ring */
.seat-ring {
  position: relative;
  width: min(78vmin, 700px);
  height: min(78vmin, 700px);
}
.seat {
  position: absolute;
  width: 110px;
  height: 130px;
  margin-left: -55px;
  margin-top: -65px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  text-align: center;
  background: var(--bg-2);
  border-radius: 14px;
  padding: 0.5rem 0.4rem;
  border: 3px solid transparent;
  font-size: 0.85rem;
  box-shadow: var(--shadow-sm);
}
.seat {
  transition: transform 380ms ease, box-shadow 380ms ease, border-color 380ms ease, opacity 280ms ease;
}
.seat.empty {
  background: rgba(255, 255, 255, 0.55);
  border-style: dashed;
  border-color: var(--bg-3);
  cursor: pointer;
  color: var(--muted);
}
.seat.empty:hover { background: rgba(255, 255, 255, 0.85); }
.seat.taken { border-color: var(--bg-3); }
.seat.ready { border-color: var(--ok); }
.seat.active {
  border-color: var(--accent);
  border-width: 4px;
  transform: scale(1.16);
  z-index: 5;
  animation: seat-active-pulse 1.4s ease-in-out infinite;
}
@keyframes seat-active-pulse {
  0%, 100% {
    box-shadow:
      0 0 0 4px rgba(220, 38, 38, 0.35),
      0 0 28px 8px rgba(220, 38, 38, 0.55),
      var(--shadow);
  }
  50% {
    box-shadow:
      0 0 0 8px rgba(220, 38, 38, 0.45),
      0 0 56px 16px rgba(220, 38, 38, 0.85),
      var(--shadow);
  }
}
.seat.disconnected { opacity: 0.45; }
.seat .name { font-weight: 700; }
.seat .count { font-size: 1.3rem; font-weight: 800; }
.seat .count-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
}
.seat .hand-icons {
  display: inline-flex;
  align-items: flex-end;
}
.seat .hand-icon {
  display: inline-block;
  width: 8px;
  height: 14px;
  background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 50%, #1d4ed8 100%);
  border: 1px solid #fff;
  border-radius: 2px;
  margin-left: -3px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.2);
}
.seat .hand-icon:first-child { margin-left: 0; }
.seat.active .hand-icon { transform: translateY(-1px); }
.seat .badge {
  display: inline-block;
  width: 1rem; height: 1rem; border-radius: 50%;
  border: 2px solid #0003;
  box-shadow: inset 0 1px 2px #0002;
}

/* Compact lobby seat: roomy circular tile with big number / avatar so a player
   walking up to the table can read it from across the seat. */
.seat-ring.compact .seat {
  width: 112px;
  height: 124px;
  margin-left: -56px;
  margin-top: -56px;
  border-radius: 50%;
  padding: 0;
  gap: 0;
  background: transparent;
  border: none;
  box-shadow: none;
  overflow: visible;
}
.seat-ring.compact .seat.empty,
.seat-ring.compact .seat.taken,
.seat-ring.compact .seat.ready {
  background: transparent;
  border: none;
  box-shadow: none;
}
.seat-ring.compact .seat .seat-avatar {
  width: 92px;
  height: 92px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  color: #fff;
  font-size: 2.4rem;
  text-shadow: 0 1px 2px rgba(0,0,0,0.4);
  border: 4px solid var(--bg-3);
  box-shadow: var(--shadow-sm);
  transition: border-color 280ms ease, transform 280ms ease, box-shadow 280ms ease;
}
.seat-ring.compact .seat.ready .seat-avatar {
  border-color: var(--ok);
  box-shadow: 0 0 0 6px rgba(34, 197, 94, 0.22), var(--shadow-sm);
}
.seat-ring.compact .seat.disconnected .seat-avatar { opacity: 0.4; }
.seat-ring.compact .seat .seat-name {
  margin-top: 6px;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--fg);
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
}
.seat-ring.compact .seat.empty .seat-num {
  width: 92px;
  height: 92px;
  border-radius: 50%;
  border: 3px dashed var(--bg-3);
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--fg);
  font-weight: 800;
  font-size: 2.6rem;
  line-height: 1;
  animation: empty-seat-glow 2.2s ease-in-out infinite;
}
.seat-ring.compact .seat.empty { cursor: pointer; }
.seat-ring.compact .seat.empty:hover .seat-num {
  background: #fff;
  border-color: var(--accent);
  color: var(--accent);
}
@keyframes empty-seat-glow {
  0%, 100% {
    box-shadow:
      0 0 0 0 rgba(220, 38, 38, 0.0),
      0 0 12px 2px rgba(220, 38, 38, 0.18);
    border-color: var(--bg-3);
  }
  50% {
    box-shadow:
      0 0 0 6px rgba(220, 38, 38, 0.18),
      0 0 28px 8px rgba(220, 38, 38, 0.42);
    border-color: var(--accent);
  }
}
@media (prefers-reduced-motion: reduce) {
  .seat-ring.compact .seat.empty .seat-num { animation: none; }
}

/* PHC seat-pick grid — flat numbered tiles, no ring layout. */
.phc-seat-prompt {
  text-align: center;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.35;
  color: var(--fg);
  max-width: 28rem;
  padding: 0 0.5rem;
}
.seat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.7rem;
  width: 100%;
  max-width: 28rem;
  padding: 0 0.25rem;
}
.seat-grid-item {
  aspect-ratio: 1 / 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  padding: 0.5rem 0.35rem;
  border-radius: 14px;
  border: 2px solid var(--bg-3);
  background: var(--bg-2);
  font-family: inherit;
  cursor: default;
  color: var(--fg);
  transition: transform 120ms ease, border-color 200ms ease, background 200ms ease;
}
.seat-grid-item.empty {
  background: rgba(255, 255, 255, 0.6);
  border-style: dashed;
  color: var(--muted);
}
.seat-grid-item.taken {
  background: var(--bg-2);
  border-color: var(--bg-3);
  opacity: 0.85;
}
.seat-grid-item.ready { border-color: var(--ok); }
.seat-grid-item.mine {
  border-color: var(--accent);
  background: rgba(220, 38, 38, 0.08);
  opacity: 1;
}
.seat-grid-item.tappable {
  cursor: pointer;
  background: rgba(255, 255, 255, 0.95);
  color: var(--fg);
}
.seat-grid-item.tappable:hover,
.seat-grid-item.tappable:active {
  transform: scale(0.97);
  border-color: var(--accent);
  background: #fff;
}
.seat-grid-item:disabled { cursor: not-allowed; }
.seat-grid-number {
  font-size: 1.8rem;
  font-weight: 800;
  line-height: 1;
}
.seat-grid-name {
  font-size: 0.7rem;
  font-weight: 600;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
@media (max-width: 360px) {
  .seat-grid { grid-template-columns: repeat(4, 1fr); gap: 0.5rem; }
  .seat-grid-number { font-size: 1.5rem; }
}

/* Header status dot — replaces the "● live" text label. */
.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--muted);
  flex-shrink: 0;
}
.status-dot.status-connected   { background: var(--ok); box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.18); }
.status-dot.status-connecting  { background: #f59e0b; }
.status-dot.status-disconnected{ background: #ef4444; }

/* Kebab menu (header overflow). */
.kebab { position: relative; }
.kebab-trigger {
  font-size: 1.4rem;
  line-height: 1;
  padding: 0.3rem 0.7rem;
  min-height: 36px;
  min-width: 36px;
}
.kebab-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 180px;
  background: var(--bg-1, #fff);
  border: 1px solid var(--bg-3);
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18);
  padding: 0.35rem;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.kebab-item {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.7rem 0.9rem;
  background: transparent;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  text-align: left;
  cursor: pointer;
  color: var(--fg);
  min-height: 44px;
}
.kebab-item:hover, .kebab-item:focus-visible {
  background: var(--bg-2);
  outline: none;
}
.kebab-item-danger { color: #b91c1c; font-weight: 600; }
.kebab-item-danger:hover { background: rgba(239, 68, 68, 0.1); }

/* Bottom action bar (pinned, 90% width) — used by lobby Ready / Start CTA. */
.action-bar {
  position: fixed;
  left: 50%;
  bottom: max(1rem, env(safe-area-inset-bottom, 0));
  transform: translateX(-50%);
  width: min(90%, 560px);
  display: flex;
  z-index: 50;
  pointer-events: none;
}
.action-bar > * { pointer-events: auto; }
.action-bar .primary-cta {
  flex: 1;
  font-size: 1.35rem;
  font-weight: 800;
  padding: 1.1rem 1.4rem;
  min-height: 64px;
  border-radius: 16px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.18);
}
.action-bar .primary-cta:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
/* Reserve room so content doesn't sit behind the bar (only when one is rendered). */
.phc-main:has(.action-bar),
.tdc-table:has(.action-bar) {
  padding-bottom: calc(96px + env(safe-area-inset-bottom, 0));
}

/* Cards */
.card-face {
  width: 84px;
  height: 120px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  color: white;
  border: 4px solid #fff;
  outline: 2px solid #0003;
  box-shadow: var(--shadow);
  text-shadow: 0 1px 2px #0006;
  user-select: none;
  position: relative;
}
.card-face.small { width: 56px; height: 80px; font-size: 1rem; border-width: 3px; }
.card-face.tiny { width: 32px; height: 48px; font-size: 0.7rem; border-width: 2px; }
.card-face .label {
  position: absolute;
  bottom: 4px;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.card-face .symbol { font-size: 2.4rem; }
.card-face.small .symbol { font-size: 1.6rem; }
.card-face.tiny .symbol { font-size: 1rem; }
.color-red { background: var(--red); }
.color-yellow { background: var(--yellow); color: #2a1a00; text-shadow: 0 1px 2px #fff8; }
.color-green { background: var(--green); }
.color-blue { background: var(--blue); }
.color-wild {
  background: linear-gradient(135deg, var(--red) 0%, var(--yellow) 33%, var(--green) 66%, var(--blue) 100%);
}
.card-back {
  background: linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%);
  border: 4px solid #fff;
  outline: 2px solid #0003;
  width: 84px;
  height: 120px;
  border-radius: 12px;
  box-shadow: var(--shadow);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: #fff7;
  font-size: 1.8rem;
  letter-spacing: 0.05em;
  font-style: italic;
  text-shadow: 0 2px 4px #0007;
}
.card-back.small { width: 56px; height: 80px; font-size: 1.2rem; border-width: 3px; }
.card-back.tiny { width: 32px; height: 48px; font-size: 0.7rem; border-width: 2px; }

/* Discard / deck stack */
.pile { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }
.pile-label { font-size: 0.85rem; color: var(--muted); text-transform: uppercase; letter-spacing: 0.08em; font-weight: 700; }
.deck-stack {
  position: relative;
  width: 84px;
  height: 120px;
  cursor: pointer;
}
.deck-stack > * { position: absolute; }
.deck-stack > *:nth-child(1) { transform: translate(-3px, -3px) rotate(-2deg); }
.deck-stack > *:nth-child(2) { transform: translate(0px, 0px); }

/* PHC */
.phc-root {
  min-height: 100vh;
  display: grid;
  grid-template-rows: auto 1fr auto;
}
.phc-header {
  padding: 0.9rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  border-bottom: 2px solid var(--bg-3);
  background: rgba(255, 248, 230, 0.85);
}
.phc-main {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
}
.phc-hand {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  justify-content: center;
}
.phc-hand .card-face {
  cursor: pointer;
  transition: transform 120ms;
}
.phc-hand .card-face.playable {
  outline: 3px solid var(--accent);
  outline-offset: 2px;
}
.phc-hand .card-face.unplayable {
  filter: grayscale(0.5);
  opacity: 0.55;
}
.phc-hand .card-face:active { transform: translateY(2px) scale(0.98); }
.phc-hand.waiting { filter: grayscale(0.55) brightness(0.92); opacity: 0.7; transition: filter 200ms, opacity 200ms; }
.phc-hand.waiting .card-face { cursor: default; }

/* Turn banner (PHC) */
.turn-banner {
  width: 100%;
  max-width: 460px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.85rem 1rem;
  border-radius: 14px;
  font-weight: 800;
  font-size: 1.15rem;
  box-shadow: var(--shadow-sm);
  border: 2px solid transparent;
  text-align: center;
}
.turn-banner .turn-meta {
  margin-left: auto;
  font-size: 0.8rem;
  font-weight: 600;
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.turn-banner.my-turn {
  background: linear-gradient(135deg, var(--accent) 0%, #f97316 100%);
  color: #fff;
  border-color: #fff;
  box-shadow: 0 0 0 3px rgba(220,38,38,0.25), var(--shadow);
  animation: pulse-mine 1.6s ease-in-out infinite;
}
.turn-banner.not-my-turn {
  background: var(--bg-soft);
  color: var(--muted);
  border-color: var(--bg-3);
}
.turn-banner .turn-badge {
  display: inline-block;
  width: 1.1rem;
  height: 1.1rem;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #0003, inset 0 1px 2px #0002;
}
@keyframes pulse-mine {
  0%, 100% { box-shadow: 0 0 0 3px rgba(220,38,38,0.20), var(--shadow); }
  50%      { box-shadow: 0 0 0 6px rgba(220,38,38,0.35), var(--shadow); }
}
.phc-controls {
  position: sticky;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.8rem;
  border-top: 2px solid var(--bg-3);
  background: rgba(255, 248, 230, 0.95);
  backdrop-filter: blur(6px);
}
.phc-controls-row { display: flex; gap: 0.5rem; }
.phc-controls-row button { flex: 1; }

.toast {
  position: fixed;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: var(--danger);
  color: #fff;
  padding: 0.7rem 1.2rem;
  border-radius: 999px;
  font-weight: 700;
  z-index: 100;
  box-shadow: var(--shadow);
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(40, 20, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 1rem;
  backdrop-filter: blur(4px);
}
.modal {
  background: var(--bg-2);
  padding: 1.6rem;
  border-radius: 16px;
  max-width: 420px;
  width: 100%;
  box-shadow: var(--shadow);
  border: 1px solid #f3e3bf;
}

.color-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
}
.color-grid button {
  height: 80px;
  font-size: 1.1rem;
  color: white;
  text-shadow: 0 1px 2px #0008;
}

.qr-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
}

.code-display {
  font-size: 3.4rem;
  font-weight: 900;
  letter-spacing: 0.5rem;
  text-align: center;
  color: var(--accent);
  text-shadow: 0 2px 0 #fff8;
}

.winner-banner {
  font-size: 2.4rem;
  font-weight: 900;
  text-align: center;
  background: linear-gradient(90deg, var(--red) 0%, var(--yellow) 33%, var(--green) 66%, var(--blue) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin: 1rem 0;
}

.history-list { display: flex; flex-direction: column; gap: 0.4rem; }
.history-list .item {
  display: flex; gap: 1rem;
  padding: 0.55rem 0.85rem;
  background: var(--bg-soft);
  border: 1px solid var(--bg-3);
  border-radius: 10px;
}

/* Discard-pile card landing animation. Wrapper is keyed on card id so React
   remounts the node and the CSS animation fires on each new play. The source
   offset (--from-x/--from-y) is set inline by TdcView so the card flies from
   the playing seat into the discard pile; falls back to a drop-from-above when
   the source isn't known (e.g. initial deal). */
.discard-stack {
  position: relative;
  display: inline-block;
}
.discard-shadow {
  position: absolute;
  width: 84px;
  height: 120px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: 0 2px 6px rgba(0,0,0,0.18);
  pointer-events: none;
}
.discard-shadow.shadow-1 {
  top: -3px;
  left: -3px;
  transform: rotate(-3deg);
  z-index: 1;
}
.discard-shadow.shadow-2 {
  top: -6px;
  left: -7px;
  transform: rotate(-6deg);
  z-index: 0;
}
.discard-stack .discard-slot { position: relative; z-index: 2; }
.discard-slot {
  display: inline-block;
  animation: card-land 620ms cubic-bezier(0.22, 0.85, 0.3, 1.05);
}
@keyframes card-land {
  0% {
    opacity: 0;
    transform: translate(var(--from-x, 0px), var(--from-y, -28px))
               rotate(var(--from-rot, -10deg)) scale(1.04);
  }
  55% {
    opacity: 1;
    transform: translate(0, 2px) rotate(2deg) scale(1.04);
  }
  100% {
    opacity: 1;
    transform: translate(0, 0) rotate(0deg) scale(1);
  }
}

/* TDC replay-vote panel — visible to the whole table during REPLAY_PROMPT.
   Shows tally, per-seat vote, and (only on a split) the operator buttons. */
.replay-panel {
  position: absolute;
  top: 8%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.96);
  border: 2px solid var(--bg-3);
  border-radius: 18px;
  padding: 1.1rem 1.3rem;
  box-shadow: 0 12px 32px rgba(0,0,0,0.18);
  z-index: 6;
  text-align: center;
  min-width: min(520px, 92%);
}
.replay-panel-title {
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--accent);
  margin-bottom: 0.4rem;
}
.replay-panel-tally {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 0.7rem;
}
.replay-panel-tally .tally-yes { color: var(--ok, #16a34a); }
.replay-panel-tally .tally-no  { color: #dc2626; }
.replay-panel-seats {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.7rem;
}
.replay-seat {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  background: var(--bg-2);
  border: 2px solid var(--bg-3);
  font-weight: 600;
}
.replay-seat .replay-seat-vote { font-weight: 800; font-size: 1.05rem; }
.replay-seat-yes { border-color: var(--ok, #16a34a); background: rgba(34, 197, 94, 0.12); }
.replay-seat-yes .replay-seat-vote { color: var(--ok, #16a34a); }
.replay-seat-no  { border-color: #dc2626; background: rgba(220, 38, 38, 0.12); }
.replay-seat-no .replay-seat-vote  { color: #dc2626; }
.replay-seat-pending { opacity: 0.7; }
.replay-seat-pending .replay-seat-vote { color: var(--muted); }
.replay-panel-actions { margin-top: 0.5rem; }
.replay-panel-actions .row { justify-content: center; gap: 0.7rem; }
.replay-panel-actions button { font-size: 1.05rem; padding: 0.7rem 1.2rem; }

/* PHC waiting-for-others row after casting a replay vote. */
.replay-waiting { justify-content: center; text-align: center; }

/* TDC lobby status strip — single line above the footer with player counts
   and the kick-hint. Sits below the seat ring so it never overlaps seats. */
.lobby-status-strip {
  position: absolute;
  left: 50%;
  bottom: 0.6rem;
  transform: translateX(-50%);
  white-space: nowrap;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.85);
  color: var(--muted);
  font-size: 0.95rem;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
  z-index: 4;
}

/* Persistent "Now / Up next" strip on the TDC, sits at the bottom of the
   table area so the family always sees who is playing and who is on deck. */
.up-next-strip {
  position: absolute;
  left: 50%;
  bottom: 1rem;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 1.2rem;
  padding: 0.6rem 1.1rem;
  background: rgba(255, 255, 255, 0.92);
  border: 2px solid var(--bg-3);
  border-radius: 999px;
  box-shadow: 0 6px 18px rgba(0,0,0,0.14);
  font-size: 1.05rem;
  font-weight: 600;
  z-index: 4;
  animation: up-next-pop 380ms cubic-bezier(0.2, 0.8, 0.3, 1.1);
}
.up-next-strip .up-next-now,
.up-next-strip .up-next-next {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.up-next-strip .up-next-label {
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  color: var(--muted);
  font-weight: 700;
}
.up-next-strip .up-next-name { font-weight: 800; }
.up-next-strip .up-next-arrow {
  font-size: 1.4rem;
  color: var(--accent);
  font-weight: 800;
}
@keyframes up-next-pop {
  0%   { opacity: 0; transform: translate(-50%, 6px) scale(0.96); }
  100% { opacity: 1; transform: translate(-50%, 0)   scale(1); }
}

/* Wild-color flash: full-screen radial wash in the chosen color when a player
   resolves AWAITING_COLOR. */
.color-flash {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 30;
  animation: color-flash 750ms ease-out forwards;
}
.color-flash.flash-red    { background: radial-gradient(circle at center, rgba(239, 68, 68, 0.55) 0%, transparent 65%); }
.color-flash.flash-yellow { background: radial-gradient(circle at center, rgba(250, 204, 21, 0.6)  0%, transparent 65%); }
.color-flash.flash-green  { background: radial-gradient(circle at center, rgba(34, 197, 94, 0.55) 0%, transparent 65%); }
.color-flash.flash-blue   { background: radial-gradient(circle at center, rgba(37, 99, 235, 0.55) 0%, transparent 65%); }
@keyframes color-flash {
  0%   { opacity: 0; }
  25%  { opacity: 1; }
  100% { opacity: 0; }
}

/* Direction arrow flip when reverse is played (TDC discard label). */
.direction-arrow {
  display: inline-block;
  animation: direction-flip 600ms ease-in-out;
}
@keyframes direction-flip {
  0%   { transform: rotate(0deg) scale(1); }
  40%  { transform: rotate(180deg) scale(1.4); }
  100% { transform: rotate(360deg) scale(1); }
}

/* PHC hand: stagger newly-mounted cards in. Fires on initial deal (7 cards
   stagger together) and on each subsequent draw (single slide-in). */
.phc-hand .card-face {
  animation: card-deal 360ms cubic-bezier(0.2, 0.85, 0.35, 1.05) backwards;
}
.phc-hand .card-face:nth-child(1)  { animation-delay: 20ms; }
.phc-hand .card-face:nth-child(2)  { animation-delay: 80ms; }
.phc-hand .card-face:nth-child(3)  { animation-delay: 140ms; }
.phc-hand .card-face:nth-child(4)  { animation-delay: 200ms; }
.phc-hand .card-face:nth-child(5)  { animation-delay: 260ms; }
.phc-hand .card-face:nth-child(6)  { animation-delay: 320ms; }
.phc-hand .card-face:nth-child(7)  { animation-delay: 380ms; }
.phc-hand .card-face:nth-child(n+8){ animation-delay: 440ms; }
@keyframes card-deal {
  0%   { opacity: 0; transform: translateY(48px) scale(0.78) rotate(-6deg); }
  100% { opacity: 1; transform: translateY(0) scale(1) rotate(0deg); }
}

/* TDC turn-change announcement overlay. Mounted briefly when currentSeatIndex
   changes; auto-clears after the keyframe finishes. */
.turn-announce {
  position: fixed;
  top: 22%;
  left: 50%;
  transform: translateX(-50%);
  padding: 1.1rem 1.8rem;
  background: linear-gradient(135deg, var(--accent) 0%, #f97316 100%);
  color: #fff;
  font-size: clamp(1.4rem, 2.6vw, 2rem);
  font-weight: 800;
  border-radius: 18px;
  border: 3px solid #fff;
  box-shadow: 0 12px 40px rgba(60, 30, 0, 0.32);
  z-index: 50;
  pointer-events: none;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  animation: turn-announce 1.4s ease-in-out forwards;
}
.turn-announce .turn-badge {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: inset 0 1px 3px #0003;
}
@keyframes turn-announce {
  0%   { opacity: 0; transform: translate(-50%, -10px) scale(0.92); }
  18%  { opacity: 1; transform: translate(-50%, 0) scale(1); }
  82%  { opacity: 1; transform: translate(-50%, 0) scale(1); }
  100% { opacity: 0; transform: translate(-50%, -8px) scale(0.96); }
}

/* TDC "drew a card" announcement — same shape as turn-announce but a
   distinct teal gradient so a draw doesn't get mistaken for a turn change.
   Mounted briefly when a card_drawn event arrives. */
.draw-announce {
  position: fixed;
  top: 22%;
  left: 50%;
  transform: translateX(-50%);
  padding: 1.1rem 1.8rem;
  background: linear-gradient(135deg, #0ea5e9 0%, #14b8a6 100%);
  color: #fff;
  font-size: clamp(1.4rem, 2.6vw, 2rem);
  font-weight: 800;
  border-radius: 18px;
  border: 3px solid #fff;
  box-shadow: 0 12px 40px rgba(0, 30, 60, 0.32);
  z-index: 51;
  pointer-events: none;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  animation: turn-announce 1.6s ease-in-out forwards;
}
.draw-announce .turn-badge {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: inset 0 1px 3px #0003;
}

/* Card-back flies from the centered deck out to the drawing player's seat.
   Coordinates are set inline by TdcView via --to-x / --to-y / --to-rot. */
.draw-fly {
  position: absolute;
  top: 50%;
  left: 50%;
  margin-top: -56px;
  margin-left: -40px;
  z-index: 4;
  pointer-events: none;
  animation: card-fly-to-seat 900ms cubic-bezier(0.34, 1.2, 0.6, 1) forwards;
}
@keyframes card-fly-to-seat {
  0%   { opacity: 0; transform: translate(0, 0) rotate(0deg) scale(0.8); }
  18%  { opacity: 1; transform: translate(0, -6px) rotate(0deg) scale(1.05); }
  100% {
    opacity: 0;
    transform: translate(var(--to-x, 0), var(--to-y, 0))
               rotate(var(--to-rot, 0deg)) scale(0.7);
  }
}

@media (prefers-reduced-motion: reduce) {
  .seat.active { animation: none; }
  .discard-slot { animation: none; }
  .color-flash { animation: color-flash-fade 750ms ease-out forwards; }
  @keyframes color-flash-fade { 0%,100% { opacity: 0; } 25% { opacity: 0.6; } }
  .direction-arrow { animation: none; }
  .phc-hand .card-face { animation: none; }
  .turn-announce { animation: turn-announce-fade 1.4s ease-in-out forwards; }
  @keyframes turn-announce-fade { 0%,100% { opacity: 0; } 18%,82% { opacity: 1; } }
  .draw-announce { animation: turn-announce-fade 1.6s ease-in-out forwards; }
  .draw-fly { animation: draw-fly-fade 900ms ease-out forwards; }
  @keyframes draw-fly-fade { 0% { opacity: 0; } 25%,70% { opacity: 1; } 100% { opacity: 0; } }
  .turn-banner.my-turn { animation: none; }
}
